export const launchLanguageCodes = ['de', 'en', 'es', 'fr', 'fr-CA', 'it', 'pt-BR'] as const;

export const i18nSupportedLanguageCodes = [...launchLanguageCodes, 'pt'] as const;

export type LaunchLanguageCode = (typeof launchLanguageCodes)[number];
export type LanguagePreference = LaunchLanguageCode | 'browser';

const launchLanguageCodeSet = new Set<string>(launchLanguageCodes);

export const isLaunchLanguageCode = (languageCode: string): languageCode is LaunchLanguageCode => {
  return launchLanguageCodeSet.has(languageCode);
};

export const isLanguagePreference = (languageCode: string | null | undefined): languageCode is LanguagePreference => {
  return languageCode === 'browser' || (!!languageCode && isLaunchLanguageCode(languageCode));
};

export const readLanguagePreference = (storage: Storage, key: string): LanguagePreference | undefined => {
  const storedPreference = storage.getItem(key);

  if (!storedPreference) {
    return undefined;
  }

  if (isLanguagePreference(storedPreference)) {
    return storedPreference;
  }

  storage.removeItem(key);
  return undefined;
};

export const normalizeLanguageCode = (languageCode: string): LaunchLanguageCode | 'en' => {
  if (!languageCode) return 'en';

  if (languageCode === 'fr-CA') return 'fr-CA';
  if (languageCode === 'pt-BR' || languageCode === 'pt' || languageCode.startsWith('pt-')) return 'pt-BR';

  if (languageCode.startsWith('en-')) return 'en';
  if (languageCode.startsWith('es-')) return 'es';
  if (languageCode.startsWith('fr-')) return 'fr';
  if (languageCode.startsWith('de-')) return 'de';
  if (languageCode.startsWith('it-')) return 'it';

  if (isLaunchLanguageCode(languageCode)) {
    return languageCode;
  }

  const baseLanguageCode = languageCode.split('-')[0];
  return isLaunchLanguageCode(baseLanguageCode) ? baseLanguageCode : 'en';
};

export const resolvePyrightLocale = (languageCode: string | null | undefined): Exclude<LaunchLanguageCode, 'en'> | undefined => {
  const normalizedLanguageCode = normalizeLanguageCode(languageCode ?? '');
  return normalizedLanguageCode === 'en' ? undefined : normalizedLanguageCode;
};

export const resolveLocaleFolder = (languageCode: string): string => {
  if (languageCode === 'fr-CA') {
    return 'fr-CA';
  }

  if (languageCode === 'pt' || languageCode.startsWith('pt-')) {
    return 'pt-BR';
  }

  return languageCode.split('-')[0];
};
